import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Shield, AlertTriangle, BarChart3, Settings, CheckCircle, XCircle, Clock, Eye, Flag, TrendingUp, DollarSign, Activity, Terminal
} from 'lucide-react';
import {
  adminService,
  PlatformAnalytics,
  ProviderVerificationRequest,
  DisputeCase,
  ContentModerationItem,
  SystemAlert
} from '@/services/adminService';
import { useToast } from '@/hooks/useToast';
import { Card } from '@/components/ui/Card';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SkipLink } from '@/components/ui/SkipLink';
import { ProviderVerificationPanel } from '@/components/admin/ProviderVerificationPanel';

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [activeTab, setActiveTab] = useState<'overview' | 'verification' | 'disputes' | 'moderation' | 'analytics'>('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [verificationRequests, setVerificationRequests] = useState<ProviderVerificationRequest[]>([]);
  const [disputes, setDisputes] = useState<DisputeCase[]>([]);
  const [moderationQueue, setModerationQueue] = useState<ContentModerationItem[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [
        analyticsData,
        verificationData,
        disputesData,
        moderationData,
        alertsData
      ] = await Promise.all([
        adminService.getPlatformAnalytics('MONTH'),
        adminService.getVerificationRequests('PENDING'),
        adminService.getDisputes('OPEN'),
        adminService.getModerationQueue(),
        adminService.getSystemAlerts('WARNING')
      ]);

      setAnalytics(analyticsData);
      setVerificationRequests(verificationData);
      setDisputes(disputesData);
      setModerationQueue(moderationData);
      setSystemAlerts(alertsData);
    } catch (err) {
      error('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationReview = async (
    requestId: string,
    decision: 'APPROVE' | 'REJECT' | 'REQUIRE_INFO',
    notes: string
  ) => {
    try {
      await adminService.reviewVerificationRequest(requestId, 'admin_1', decision, notes);
      success(`Request ${decision.toLowerCase()}d successfully.`);
      loadDashboardData();
    } catch (err) {
      error('Verification review failed.');
    }
  };

  const handleDisputeAssignment = async (disputeId: string) => {
    try {
      await adminService.assignDispute(disputeId, 'admin_1');
      success('Dispute assigned successfully.');
      loadDashboardData();
    } catch (err) {
      error('Assignment failed.');
    }
  };

  const handleContentModeration = async (
    itemId: string,
    decision: 'APPROVE' | 'REJECT' | 'REQUIRE_EDIT',
    notes: string
  ) => {
    try {
      await adminService.moderateContent(itemId, 'admin_1', decision, notes);
      success(`Content ${decision.toLowerCase()}ed successfully.`);
      loadDashboardData();
    } catch (err) {
      error('Moderation action failed.');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Dashboard' }]} />
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4 mt-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Platform overview and management controls.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadDashboardData} size="sm" aria-label="Refresh dashboard data">Refresh</Button>
            <Button onClick={() => navigate('/admin/settings')} size="sm" icon={<Settings className="w-4 h-4" />}>Settings</Button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex overflow-x-auto gap-2 mb-8 border-b border-gray-200 pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'verification', label: 'Verification', icon: Shield, count: verificationRequests.length },
            { id: 'disputes', label: 'Disputes', icon: AlertTriangle, count: disputes.length },
            { id: 'moderation', label: 'Moderation', icon: Flag, count: moderationQueue.length },
            { id: 'analytics', label: 'Analytics', icon: Activity },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && analytics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Users</p>
                      <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.totalUsers.toLocaleString()}</h3>
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{analytics.trends.userGrowth}%</p>
                    </div>
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><Users className="w-5 h-5" /></div>
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Revenue</p>
                      <h3 className="text-2xl font-bold text-gray-900 mt-1">€{(analytics.metrics.totalRevenue / 1000000).toFixed(2)}M</h3>
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{analytics.trends.revenueGrowth}%</p>
                    </div>
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg"><DollarSign className="w-5 h-5" /></div>
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Active Jobs</p>
                      <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.totalJobs.toLocaleString()}</h3>
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{analytics.trends.jobGrowth}%</p>
                    </div>
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-lg"><Terminal className="w-5 h-5" /></div>
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Avg Rating</p>
                      <h3 className="text-2xl font-bold text-gray-900 mt-1">{analytics.metrics.customerSatisfaction.toFixed(1)}/5.0</h3>
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{analytics.trends.satisfactionTrend}%</p>
                    </div>
                    <div className="p-3 bg-amber-50 text-amber-600 rounded-lg"><Activity className="w-5 h-5" /></div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Alerts</h3>
                  {systemAlerts.length > 0 ? (
                    <div className="space-y-4">
                      {systemAlerts.map(alert => (
                        <div key={alert.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <AlertTriangle className={`w-5 h-5 mt-0.5 ${alert.severity === 'CRITICAL' ? 'text-red-500' : 'text-amber-500'}`} />
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">{alert.type.replace('_', ' ')}</h4>
                            <p className="text-xs text-gray-600 mt-1">{alert.message}</p>
                            <span className="text-xs text-gray-400 mt-2 block">{new Date(alert.createdAt).toLocaleTimeString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">No active alerts</div>
                  )}
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Pending Actions</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-amber-50 rounded-lg border border-amber-100">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-900">Verification Requests</span>
                      </div>
                      <span className="bg-amber-200 text-amber-800 text-xs font-bold px-2 py-1 rounded-full">{verificationRequests.length}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-900">Open Disputes</span>
                      </div>
                      <span className="bg-red-200 text-red-800 text-xs font-bold px-2 py-1 rounded-full">{disputes.length}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-3">
                        <Flag className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">Moderation Queue</span>
                      </div>
                      <span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">{moderationQueue.length}</span>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {activeTab === 'verification' && (
            <ProviderVerificationPanel />
          )}

          {activeTab === 'disputes' && (
            <div className="space-y-4">
              {disputes.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">No disputes</h3>
                  <p>Everything is running smoothly.</p>
                </div>
              ) : (
                disputes.map(dispute => (
                  <Card key={dispute.id} className="p-6 border-l-4 border-l-red-500">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{dispute.type.replace('_', ' ')}</h3>
                          <Badge variant="danger">{dispute.priority}</Badge>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">"{dispute.description}"</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {dispute.createdAt.toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Settings className="w-3 h-3" /> Job ID: {dispute.jobId}</span>
                        </div>
                      </div>
                      <div>
                        {dispute.assignedTo ? (
                          <div className="text-sm text-gray-500">Assigned to: {dispute.assignedTo}</div>
                        ) : (
                          <Button size="sm" onClick={() => handleDisputeAssignment(dispute.id)}>Assign to Me</Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Other tabs can be implemented similarly with professional UI */}
          {activeTab === 'moderation' && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">Moderation Queue Interface Placeholder</p>
            </div>
          )}
          {activeTab === 'analytics' && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">Detailed Analytics Interface Placeholder</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
